import React from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';

import type { InterventionCenterStatistics as InterventionCenterStatisticsType } from '../../../common/types/statistics/fhbfStatistics';
import OptionStatistic from './OptionStatistic';
import policeMeasureForm from '../../../clients/detail/fhbfForm/interventionCenter/policeMeasureForm';
import contactForm from '../../../clients/detail/fhbfForm/interventionCenter/contactForm';
import anamnesisForm from '../../../clients/detail/fhbfForm/interventionCenter/anamnesisForm';
import FaxesStatistics from './FaxesStatistics';
import formatter from '../../../common/helpers/formatter';
import faxForm from '../../../clients/detail/fhbfForm/fax/faxForm';

type Props = {
  statistics: InterventionCenterStatisticsType;
};

function CaseStatistics({ statistics }: Props) {
  const policeStationOptions = formatter.getFieldSelectOptions('policeStation', faxForm.fields);
  const filtertedNationality = Object.keys(statistics.nationality).reduce((value, key) => {
    const keyValue = statistics.nationality[key];
    if (keyValue > 0) {
      return { ...value, [key]: keyValue };
    }
    return value;
  }, {});

  return (
    <Paper
      variant='outlined'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        margin: '24px 0',
        padding: '20px 24px',
        maxWidth: '650px',
        minWidth: '300px',
        backgroundColor: '#FAFAFA',
      }}
    >
      <Stack direction='column'>
        <Typography fontSize='1.2rem' fontWeight='bold' mb={2}>
          Fälle
        </Typography>
        <Typography>Fälle gesamt: {statistics.totalNumber}</Typography>
        <Typography>Anzahl der Selbstmelderinnen: {statistics.numberOfSelfReporter}</Typography>
        <Typography>Anzahl der Irrläuferinnen: {statistics.numberOfWaywards}</Typography>
        <Typography>Anzahl der Wiederholungsfälle: {statistics.numberOfRepeatCases}</Typography>
        <Typography fontWeight='bold' mt={2}>
          Faxe
        </Typography>
        <Typography>Zahl der Faxe: {statistics.numberOfFaxes}</Typography>
        {statistics.faxesPerPoliceStations.map((faxesPerPoliceStation) => (
          <FaxesStatistics policeStationOptions={policeStationOptions} faxesPerPoliceStation={faxesPerPoliceStation} />
        ))}
        {statistics.numberOfFaxesPerMonth.map((faxPerMonth) => (
          <Typography key={faxPerMonth.dateLabel}>
            {faxPerMonth.dateLabel}: {faxPerMonth.numberOfFaxes}
          </Typography>
        ))}
        <OptionStatistic
          fieldKey='policeMeasure'
          fields={policeMeasureForm.fields}
          statistic={statistics.policeMeasure}
        />
        <OptionStatistic
          fieldKey='typeOfInitialContact'
          fields={contactForm.fields}
          statistic={statistics.typeOfInitialContact}
        />
        <OptionStatistic fieldKey='age' fields={anamnesisForm.fields} statistic={statistics.age} />
        <OptionStatistic fieldKey='origin' fields={anamnesisForm.fields} statistic={statistics.origin} />
        <OptionStatistic fieldKey='nationality' fields={anamnesisForm.fields} statistic={filtertedNationality} />
        <Typography>Anzahl der Frauen mit minderjährigen Kindern: {statistics.numberOfChildren}</Typography>
        <Typography>Anzahl der anwesenden Kinder: {statistics.numberOfChildrenPresent}</Typography>
        <Typography>
          Anzahl der Kinder im Alter von 0 - 3 Jahre: {statistics.numberOfChildrenZeroToThreeYears}
        </Typography>
        <Typography>Anzahl der Kinder im Alter von 4 - 6 Jahre: {statistics.numberOfChildrenFourToSixYears}</Typography>
        <Typography>
          Anzahl der Kinder im Alter von 7 - 13 Jahre: {statistics.numberOfChildrenSevenToThirteenYears}
        </Typography>
        <Typography>
          Anzahl der Kinder im Alter von 14 - 17 Jahre: {statistics.numberOfChildrenFourteenToSeventeenYears}
        </Typography>

        <Typography>Anzahl der schwangeren Frauen: {statistics.numberOfPregnancy}</Typography>
        <Typography>Interesse Stabilisierungsgruppe: {statistics.numberOfStabilizationGroup}</Typography>
        <Typography>Interesse an traumasensiblen Intensivangebot: {statistics.numberOfTraumaCounseling}</Typography>
      </Stack>
    </Paper>
  );
}

export default CaseStatistics;
